import React, { useContext, useCallback, useMemo, useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Grid,
  InputAdornment,
  Pagination,
  Stack,
  Backdrop,
  useTheme,
  Typography,
  MenuItem,
} from "@mui/material";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PAGE_SIZE } from "../../Services/common/const";
import Title from "../../Components/Title";
import ShortcutButton from "../../Components/ShortcutButton";
import { PreferencesContext } from "../../PreferencesContext";
import MissingConsumer from "../../Components/MissingConsumer";
import { ExportFilesFunction, numberFormat } from "../../shared/CommonFuctions";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import SplashComponent from "../../Navigation/splash";
import { AlertType } from "../../shared/Modal/AlertType";
import { EncryptDecryptService } from "../../Services/common/EncryptionService";
import { StyledMenu } from "../../Components/StyleMenus";
import { format } from "date-fns";
import CustomDateRangePicker from "../../Components/CustomDateRange";
import { GetPurchaseSummaryByProduct } from "../../Services/PurchaseService";
import { GetPurchaseSummaryByProductReport } from "../../Services/ReportService";

const parameters = {
  consumerId: 25,
  branchId: 1,
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  lessThanValue: "",
};

type Order = "asc" | "desc" | "";

interface Data {
  productId: number;
  productName: string;
  count: number;
  freeQuantity: number;
  quantity: number;
  totalQuantity: number;
  totalAmount: number;
  billAmount: number;
}

interface ApiResponse {
  data: {
    data: {
      purchaseSummaryByProductListCollection: Data[];
      totalCount: number;
    };
  };
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const PurchaseSummaryByProduct = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [isLoading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [param, setParam] = useState(parameters);
  const [fromDate, setFromDate] = useState<any>(userContext.financialYearStartDate);
  const [toDate, setToDate] = useState<any>(userContext.financialYearEndDate);
  const [order, setOrder] = useState<Order>("");
  const [orderBy, setOrderBy] = useState<keyof Data>("productName");
  const [resultData, setResultData] = useState<Data[]>([]);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const mobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const matches = useMediaQuery("(max-width:425px)");
  const edService = new EncryptDecryptService();
  const openExportType = Boolean(anchorEl2);

  const pageInfo = useMemo(() => ({
    startIndex: (page - 1) * PAGE_SIZE + 1,
    endIndex: (page - 1) * PAGE_SIZE + resultData.length,
    totalPages: Math.ceil(totalCount / PAGE_SIZE)
  }), [page, resultData.length, totalCount]);

  const handleSearch = useCallback(
    (val: string) => {
      setSearchValue(val);
      setPage(1);
      setParam(prev => ({ ...prev, searchValue: val, pageIndex: 1 }));
    },
    []
  );

  const handlePageChange = useCallback(
    (event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
      setParam(prev => ({ ...prev, pageIndex: value }));
    },
    []
  );

  const handleExport = useCallback(
    async (type: string) => {
      setAnchorEl2(null);
      setLoading(true);
      try {
        const params = new URLSearchParams({
          reportName: "PurchaseSummaryByProduct",
          reportType: type,
          consumerId: userContext.consumerId.toString(),
          branchId: userContext.branchId.toString(),
          firmId: userContext.firmId.toString(),
          financialYear: userContext.financialYear.toString(),
          searchValue: param.searchValue || "",
          mode: "EXPORT",
          startDate: format(new Date(fromDate), "yyyy-MM-dd"),
          endDate: format(new Date(toDate), "yyyy-MM-dd"),
          isFreeQuantity: userContext.enableFreeQuantity.toString(),
          fileName: `PurchaseSummaryByProduct${format(new Date(fromDate), "_yyyyMMdd")}${format(new Date(toDate), "_yyyyMMdd")}.${type === "pdf" ? "pdf" : "xls"}`,
          isForDownload: "1"
        });

        const result: any = await GetPurchaseSummaryByProductReport(params);
        if (result?.data?.data?.fileData) {
          ExportFilesFunction(params.get("fileName")!, type, result.data.data.fileData);
        }
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: typeof e === "string" ? e : e?.message,
          alertType: AlertType.Error,
        });
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, param.searchValue, userContext]
  );

  const handleMoveToPurchase = useCallback(
    async (productId: number) => {
      try {
        const encProductId = edService.encrypt(productId.toString());
        navigate({
          pathname: "/purchase-details-by-product",
          search: `?q=${encodeURIComponent(encProductId || "")}`,
        });
      } catch (e: any) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: typeof e === "string" ? e : e?.message,
          alertType: AlertType.Error,
        });
      }
    },
    [navigate, edService, userContext, updateUserContext]
  );

  const fetchPurchaseList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        consumerId: userContext.consumerId.toString(),
        firmId: userContext.firmId.toString(),
        branchId: userContext.branchId.toString(),
        pageIndex: page.toString(),
        pageSize: PAGE_SIZE.toString(),
        searchValue: param.searchValue || "",
        financialYear: userContext.financialYear,
        startDate: format(new Date(fromDate), "yyyy-MM-dd"),
        endDate: format(new Date(toDate), "yyyy-MM-dd")
      });

      const result = await GetPurchaseSummaryByProduct(params) as ApiResponse;
      if (result?.data?.data?.purchaseSummaryByProductListCollection) {
        const { purchaseSummaryByProductListCollection, totalCount: dataCount } = result.data.data;
        setResultData(purchaseSummaryByProductListCollection);
        setTotalCount(dataCount);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: typeof e === "string" ? e : e?.message,
        alertType: AlertType.Error,
      });
    } finally {
      setLoading(false);
    }
  }, [page, param, fromDate, toDate, userContext]);

  useEffect(() => {
    fetchPurchaseList();
  }, [fetchPurchaseList]);

  const handleCloseExportType = () => {
    setAnchorEl2(null);
  };

  const handleClickExportType = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl2(event.currentTarget);
  };

  const openSalesReportPopup = async (type: string) => {
    await handleExport(type);
  };

  const TableHeader = React.memo(() => (
    <TableHead>
      <TableRow>
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="left"
        >
          {"Product Name"}
        </TableCell>
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="center"
        >
          {"Bills"}
        </TableCell>
        {userContext.enableFreeQuantity && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Free Quantity"}
          </TableCell>
        )}
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="right"
        >
          {"Quantity"}
        </TableCell>
        {userContext.enableFreeQuantity && (
          <TableCell
            sx={{
              display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
              fontWeight: "bold",
              minWidth: "80px",
            }}
            align="right"
          >
            {"Total Quantity"}
          </TableCell>
        )}
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="right"
        >
          {"Total Amount"}
        </TableCell>
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell", lg: "table-cell" },
            fontWeight: "bold",
            minWidth: "80px",
          }}
          align="right"
        >
          {"Bill Amount"}
        </TableCell>
      </TableRow>
    </TableHead>
  ));

  const TableRowComponent = React.memo(({
    item,
    handleMoveToPurchase,
  }: {
    item: Data;
    handleMoveToPurchase: (id: number) => void;
  }) => (
    <TableRow hover role="checkbox" tabIndex={-1}>
      <TableCell
        sx={{
          display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
        }}
        align="left"
      >
        {item?.productName}
      </TableCell>
      <TableCell
        sx={{
          display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
        }}
        align="center"
      >
        <Button
          disableRipple
          color="inherit"
          onClick={() => handleMoveToPurchase(item.productId)}
          className="manuButton-link"
          style={{ backgroundColor: "transparent" }}
        >
          <span className="text-link" style={{ fontSize: "15px" }}>
            {item?.count}
          </span>
        </Button>
      </TableCell>
      {userContext.enableFreeQuantity && (
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
          }}
          align="right"
        >
          {numberFormat(item?.freeQuantity, 3, 3)}
        </TableCell>
      )}
      <TableCell
        sx={{
          display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
        }}
        align="right"
      >
        {numberFormat(item?.quantity, 3, 3)}
      </TableCell>
      {userContext.enableFreeQuantity && (
        <TableCell
          sx={{
            display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
          }}
          align="right"
        >
          {numberFormat(item?.totalQuantity, 3, 3)}
        </TableCell>
      )}
      <TableCell
        sx={{
          display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
        }}
        align="right"
      >
        {numberFormat(item?.totalAmount)}
      </TableCell>
      <TableCell
        sx={{
          display: { xs: "table-cell", sm: "table-cell", md: "table-cell" },
        }}
        align="right"
      >
        {numberFormat(item?.billAmount)}
      </TableCell>
    </TableRow>
  ));

  const TableFooter = React.memo(({ resultData }: { resultData: Data[] }) => {
    const totals = useMemo(() => ({
      count: resultData.reduce((acc, curr) => acc + (curr?.count || 0), 0),
      freeQuantity: resultData.reduce((acc, curr) => acc + (curr?.freeQuantity || 0), 0),
      quantity: resultData.reduce((acc, curr) => acc + (curr?.quantity || 0), 0),
      totalQuantity: resultData.reduce((acc, curr) => acc + (curr?.totalQuantity || 0), 0),
      totalAmount: resultData.reduce((acc, curr) => acc + (curr?.totalAmount || 0), 0),
      billAmount: resultData.reduce((acc, curr) => acc + (curr?.billAmount || 0), 0)
    }), [resultData]);

    return (
      <TableRow hover tabIndex={-1} key={"0"}>
        <TableCell className="bold-600">Total</TableCell>
        <TableCell align="center" className="bold-600">
          {totals.count}
        </TableCell>
        {userContext.enableFreeQuantity && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.freeQuantity, 3, 3)}
          </TableCell>
        )}
        <TableCell align="right" className="bold-600">
          {numberFormat(totals.quantity, 3, 3)}
        </TableCell>
        {userContext.enableFreeQuantity && (
          <TableCell align="right" className="bold-600">
            {numberFormat(totals.totalQuantity, 3, 3)}
          </TableCell>
        )}
        <TableCell align="right" className="bold-600">
          {numberFormat(totals.totalAmount)}
        </TableCell>
        <TableCell align="right" className="bold-600">
          {numberFormat(totals.billAmount)}
        </TableCell>
      </TableRow>
    );
  });

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          backgroundColor: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <SplashComponent />
      </Backdrop>
      <Title pageTitle="Purchase Summary By Product" />

      {userContext.consumerId > 0 ? (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}
        >
          <Grid
            className="title-button-responsive"
            item
            xs={6}
            md={3}
            sx={{
              display: "flex !important",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h4 className="page-title">Purchase Summary By Product</h4>

            <Grid
              item
              xs={6}
              md={3}
              marginRight={0}
              textAlign="end"
              paddingTop="0px"
            >
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                size="medium"
              >
                <ShortcutButton label="ESC" />
                Back
              </Button>
            </Grid>
          </Grid>
          <Grid className="paper top-space">
            <Paper elevation={4} className="paper-container">
              <Grid
                container
                spacing={1}
                sx={{ marginTop: "10px" }}
                className="search-space"
              >
                <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                  <TextField
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch(searchValue);
                      }
                    }}
                    sx={{ paddingLeft: "0px !important", width: "100%" }}
                    className="button-responsive-stock"
                    placeholder="Search..."
                    type="text"
                    variant="outlined"
                    size="small"
                    onChange={(e) => setSearchValue(e.target.value)}
                    value={searchValue}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleSearch(searchValue)}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                      endAdornment: searchValue && (
                        <IconButton onClick={() => handleSearch("")}>
                          <ClearIcon />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={9} lg={9} xl={9}>
                  <Grid
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "stretch", md: "flex-end" },
                      alignItems: { xs: "stretch", md: "flex-start", lg: "center" },
                      gap: "5px",
                      flexWrap: "wrap",
                      "& > div, & > button, & > Box": {
                        width: { xs: "100%", md: "auto" },
                      },
                    }}>
                    <CustomDateRangePicker
                      sx={{ marginTop: matches ? "10px !important" : "" }}
                      fromDate={fromDate}
                      setFromDate={setFromDate}
                      toDate={toDate}
                      setToDate={setToDate}
                      minDate={dayjs(userContext.financialYearStartDate)}
                      maxDate={dayjs(userContext.financialYearEndDate)}
                    />
                    <Button
                      className="button-responsive-stock"
                      aria-controls={
                        openExportType ? "demo-customized-menu" : undefined
                      }
                      aria-haspopup="true"
                      aria-expanded={openExportType ? "true" : undefined}
                      disabled={resultData?.length <= 0}
                      variant="outlined"
                      disableElevation
                      onClick={handleClickExportType}
                      startIcon={<DownloadIcon />}
                      endIcon={<KeyboardArrowDownSharp />}
                    >
                      Export
                    </Button>
                    <StyledMenu
                      id="demo-customized-menu"
                      MenuListProps={{
                        "aria-labelledby": "demo-customized-button",
                      }}
                      anchorEl={anchorEl2}
                      open={openExportType}
                      onClose={handleCloseExportType}
                    >
                      <MenuItem
                        disableRipple
                        onClick={() => openSalesReportPopup("pdf")}
                      >
                        PDF
                      </MenuItem>
                      <MenuItem
                        disableRipple
                        onClick={() => openSalesReportPopup("Excel")}
                      >
                        EXCEL
                      </MenuItem>
                    </StyledMenu>
                  </Grid>
                </Grid>
              </Grid>

              <TableContainer>
                <Table aria-labelledby="tableTitle" size="medium">
                  <TableHeader />
                  {resultData?.length > 0 && (
                    <TableBody>
                      {resultData.map((item: Data, index: number) => (
                        <TableRowComponent
                          key={item.productId || index}
                          item={item}
                          handleMoveToPurchase={handleMoveToPurchase}
                        />
                      ))}
                      <TableFooter resultData={resultData} />
                    </TableBody>
                  )}
                  {(!resultData || resultData.length <= 0) && (
                    <TableBody>
                      <TableRow hover tabIndex={-1} key={"0"}>
                        <TableCell colSpan={7} align="center">
                          No record(s) found
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
                {resultData?.length > 0 && (
                  <Grid
                    container
                    paddingTop={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <Grid item xs={12} sm={6} className="pagination-label">
                      {pageInfo.startIndex} - {pageInfo.endIndex} of {totalCount}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Stack
                        component="div"
                        alignItems={mobileScreen ? "center" : "end"}
                        justifyContent={"center"}
                      >
                        <Pagination
                          color="primary"
                          count={pageInfo.totalPages}
                          page={page}
                          onChange={handlePageChange}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                )}
              </TableContainer>
            </Paper>
          </Grid>
        </Box>
      ) : (
        <Box
          className="boxContainer"
          sx={{
            "& > :not(style)": {
              width: "100%",
            },
          }}
        >
          <MissingConsumer consumerId={userContext.consumerId} />
        </Box>
      )}
    </>
  );
};

export default React.memo(PurchaseSummaryByProduct);
